import {
  EExternalReferenceCategory,
  EExternalReferenceParsingMode,
  ENOPOrganType,
  EXTERNAL_REFERENCE_LEGACY_MODE,
} from '#shared';

// ─── Organ Mappings ──────────────────────────────────────────────────────────

type OrganMatchName =
  | 'ht'
  | 'hr'
  | 'hrt'
  | 'lu'
  | 'li'
  | 'ki'
  | 'liv'
  | 'liver'
  | 'live'
  | 'heart'
  | 'lung'
  | 'lungs'
  | 'liung'
  | 'kidney';

const organNameMap: Record<OrganMatchName, ENOPOrganType | null> = {
  ht: ENOPOrganType.Heart,
  hr: ENOPOrganType.Heart,
  hrt: ENOPOrganType.Heart,
  heart: ENOPOrganType.Heart,
  lu: ENOPOrganType.Lungs,
  lung: ENOPOrganType.Lungs,
  lungs: ENOPOrganType.Lungs,
  liung: ENOPOrganType.Lungs, // typo variant
  li: ENOPOrganType.Liver,
  liv: ENOPOrganType.Liver,
  live: ENOPOrganType.Liver,
  liver: ENOPOrganType.Liver,
  // Kidney support — parsing only, not in ENOPOrganType
  ki: null,
  kidney: null,
};

const organShortNameMap: Record<ENOPOrganType, string> & {
  Kidney: string;
} = {
  Heart: 'HT',
  Liver: 'LI',
  Lungs: 'LU',
  Kidney: 'KI',
};

/**
 * All organ keywords used for detection.
 * Order matters: longer variants first to avoid partial matches.
 */
const ORGAN_KEYWORDS: readonly OrganMatchName[] = [
  'kidney',
  'liver',
  'heart',
  'lungs',
  'liung',
  'live',
  'lung',
  'hrt',
  'liv',
  'ht',
  'hr',
  'lu',
  'li',
  'ki',
];

/** Organ suffixes that can be concatenated directly to donor ID */
const ORGAN_SUFFIXES = ['HTKI', 'LIKI', 'LKI', 'HT', 'LI', 'LU', 'KI'] as const;

/** Multi-organ separator pattern (after normalization) */
const MULTI_ORGAN_SEPARATORS = /[+,&]|\band\b/i;

/** Organ/organ slash notation pattern (e.g., HT/LI, LU/KI) */
const ORGAN_SLASH_PATTERN = /(HT|HR|LI|LU|KI)\s*\/\s*(HT|HR|LI|LU|KI)\b/i;

// ─── Donor Type ──────────────────────────────────────────────────────────────

type DonorType = 'DBD' | 'DCD';

/** Matches DCD/DBD including common typos */
const DONOR_TYPE_PATTERN = /\b(DCD|DBD|BDB|DBB|DCC|DCDC)\b/i;

const normalizeDonorType = (raw: string): DonorType | null => {
  const upper = raw.toUpperCase();
  if (upper === 'DCD' || upper === 'DCC' || upper === 'DCDC') {
    return 'DCD';
  }
  if (upper === 'DBD' || upper === 'BDB' || upper === 'DBB') {
    return 'DBD';
  }
  return null;
};

// ─── Suffix Flags ────────────────────────────────────────────────────────────

export interface DonorSuffix {
  /** OPO case flag (-O or -0) */
  opoCase: boolean;
  /** Re-offer number: 1 for -R1, 2 for -R2, etc. */
  reOffer: number | null;
  /** Enhanced flag (-E) */
  enhanced: boolean;
}

/**
 * Extracts suffix flags from a token like "LI-O", "HT-R1", "LI-R1O", "HT-E"
 * Returns the cleaned token and the extracted suffix.
 */
const extractSuffixes = (
  token: string,
): { cleaned: string; suffix: DonorSuffix } => {
  const suffix: DonorSuffix = {
    opoCase: false,
    reOffer: null,
    enhanced: false,
  };
  let cleaned = token;

  // Match suffix patterns at end: -O, -0, -R1, -R2, -R1O, -R10, -E
  const suffixPattern = /[-\s]+(R(\d+)O?|R(\d+)|[O0]|E)$/i;
  let match = suffixPattern.exec(cleaned);
  while (match) {
    const flag = match[1].toUpperCase();
    if (flag === 'O' || flag === '0') {
      suffix.opoCase = true;
    } else if (flag === 'E') {
      suffix.enhanced = true;
    } else if (flag.startsWith('R')) {
      // R1O, R10 → reOffer=1 + opoCase; R1 → reOffer=1; R2 → reOffer=2
      const digits = match[2] || match[3];
      suffix.reOffer = parseInt(digits, 10);
      if (flag.endsWith('O') || flag.endsWith('0')) {
        suffix.opoCase = true;
      }
    }
    cleaned = cleaned.slice(0, match.index);
    match = suffixPattern.exec(cleaned);
  }

  return { cleaned, suffix };
};

const hasSuffix = (s: DonorSuffix): boolean =>
  s.opoCase || s.reOffer !== null || s.enhanced;

// ─── Donor ID Helpers ────────────────────────────────────────────────────────

/**
 * Tokens to exclude as donor ID candidates — known keywords, organ names, etc.
 */
const EXCLUDED_TOKENS = new Set([
  // Donor types (including typos)
  'dcd',
  'dbd',
  'bdb',
  'dbb',
  'dcc',
  'dcdc',
  'dc',
  'cd',
  // Organ keywords
  'ht',
  'hr',
  'hrt',
  'lu',
  'li',
  'ki',
  'liv',
  'liver',
  'live',
  'heart',
  'lung',
  'lungs',
  'liung',
  'kidney',
  // Category prefixes
  'mx',
  'trng',
  'repo',
  'special',
  'training',
  'maintenance',
  'backup',
  'back',
  'up',
  'bu',
  'pre',
  // Metadata
  'recovery',
  'reallocation',
  'reallo',
  'preallo',
  'preallocated',
  'preallocation',
  // Misc keywords
  'and',
  'for',
  'the',
  'to',
  'of',
  'or',
  'is',
  'in',
  'at',
  'a',
  'an',
  'by',
  'on',
  'not',
  'no',
  'vs',
  'flight',
  'flights',
  'trip',
  'leg',
  'check',
  'test',
]);

/**
 * Quick check: does the text contain something that could be a donor ID?
 * Used for category detection to distinguish non-mission flights
 * from unknown missions.
 */
const hasDonorIdCandidate = (text: string): boolean => {
  const tokenPattern = /[a-zA-Z0-9]{1,10}/g;
  const tokens = text.match(tokenPattern) || [];
  return tokens.some(t => {
    if (t.length < 3) {
      return false;
    }
    if (!/\d/.test(t)) {
      return false;
    }
    if (EXCLUDED_TOKENS.has(t.toLowerCase())) {
      return false;
    }
    return true;
  });
};

// ─── Category Detection ──────────────────────────────────────────────────────

// New format patterns (both modes)
const newFmtMaintenancePattern = /^\s*MX\s*\//i;
const newFmtTrainingPattern = /^\s*TRNG\s*\//i;
const newFmtRepositionPattern = /^\s*Repo\s*\//i;
const newFmtSpecialPattern = /^\s*Special\s*[/-]/i;

// Legacy non-mission patterns (soft mode)
const legacyMaintenancePattern = /^(Maintenance|Mx\s|MX$)/i;
const legacyConfidencePattern = /confidence\s*flight/i;
const legacyMxCheckPattern =
  /^(post\s+)?mx\s+(check|test|acceptance|ferry|confidence)\s*flight/i;
const legacyMxRepoPattern =
  /^(MX\s+Repo|Repo\s+for\s+(MX|SCMX|Checkride|Conformity))/i;

const legacyTrainingPattern = /^(Training|Traning|Traing)\b/i; // typos
const legacyCheckridePattern =
  /\.(299|293|297)\b|check\s*ride|FAA\s+(Check|Checkride)/i;
const legacyNewHirePattern =
  /^(new\s+hire\s+training|crew\s+training|pilot\s+training|P91\s*TRNG|P91\s*Training)/i;
const legacyIndocPattern = /\bINDOC\b/i;

const legacyValidityPattern = /^V(a|ai)lid(it|iti|i)?y\s*Check/i;
const legacyWxAvoidancePattern = /^Wx\s*Avoidance/i;
const legacyEmptyRepoPattern = /^Empty\s*(to\s+hub|repo)/i;
const legacyHubRepoPattern = /^Hub\s*repo/i;
const legacyRepositionPattern =
  /^(Reposition|Position\s+for|HOU\s+to\s+\w+\s+position)/i;

const legacyTestPattern = /^TEST($|\s)/i;
const legacyVipPattern = /^(VIP|TMDX\s+VVIP)\b/i;
const legacyFerryPattern = /^Ferry\s*Flight/i;
const legacySamplePattern = /^(SAMPLE\s+TRIP|for\s+testing\s+purposes)/i;
const legacyForeflight = /foref?l[iy]g?ht\s+testing/i;
const legacyPreallocationOnly = /^Preallocation\s+\w{2,5}$/i;

/** Detects new-format category markers (used in both modes). */
const detectNewFormatCategory = (
  trimmed: string,
): EExternalReferenceCategory | null => {
  if (newFmtMaintenancePattern.test(trimmed)) {
    return EExternalReferenceCategory.Maintenance;
  }
  if (newFmtTrainingPattern.test(trimmed)) {
    return EExternalReferenceCategory.Training;
  }
  if (newFmtRepositionPattern.test(trimmed)) {
    return EExternalReferenceCategory.Reposition;
  }
  if (newFmtSpecialPattern.test(trimmed)) {
    return EExternalReferenceCategory.Special;
  }
  return null;
};

/** Detects legacy category markers (soft mode only). */
const detectLegacyCategorySoft = (
  trimmed: string,
): EExternalReferenceCategory | null => {
  // Maintenance
  if (legacyMaintenancePattern.test(trimmed)) {
    return EExternalReferenceCategory.Maintenance;
  }
  if (legacyMxCheckPattern.test(trimmed)) {
    return EExternalReferenceCategory.Maintenance;
  }
  if (legacyMxRepoPattern.test(trimmed)) {
    return EExternalReferenceCategory.Maintenance;
  }

  // Training
  if (legacyTrainingPattern.test(trimmed)) {
    return EExternalReferenceCategory.Training;
  }
  if (legacyCheckridePattern.test(trimmed)) {
    return EExternalReferenceCategory.Training;
  }
  if (legacyNewHirePattern.test(trimmed)) {
    return EExternalReferenceCategory.Training;
  }
  if (legacyIndocPattern.test(trimmed)) {
    return EExternalReferenceCategory.Training;
  }

  // Reposition
  if (legacyWxAvoidancePattern.test(trimmed)) {
    return EExternalReferenceCategory.Reposition;
  }
  if (legacyEmptyRepoPattern.test(trimmed)) {
    return EExternalReferenceCategory.Reposition;
  }
  if (legacyHubRepoPattern.test(trimmed)) {
    return EExternalReferenceCategory.Reposition;
  }
  if (legacyRepositionPattern.test(trimmed)) {
    return EExternalReferenceCategory.Reposition;
  }
  if (legacyFerryPattern.test(trimmed)) {
    return EExternalReferenceCategory.Reposition;
  }

  // Special
  if (legacyValidityPattern.test(trimmed)) {
    return EExternalReferenceCategory.Special;
  }
  if (legacyVipPattern.test(trimmed)) {
    return EExternalReferenceCategory.Special;
  }

  // Other non-mission
  if (legacySamplePattern.test(trimmed)) {
    return EExternalReferenceCategory.Unknown;
  }
  if (legacyForeflight.test(trimmed)) {
    return EExternalReferenceCategory.Unknown;
  }
  if (legacyPreallocationOnly.test(trimmed)) {
    return EExternalReferenceCategory.Unknown;
  }

  // Confidence flight without a donor ID
  if (legacyConfidencePattern.test(trimmed) && !hasDonorIdCandidate(trimmed)) {
    return EExternalReferenceCategory.Maintenance;
  }

  return null;
};

/**
 * Determines the flight category from external booking reference.
 */
const detectCategory = (
  reference: string,
  mode: EExternalReferenceParsingMode,
): EExternalReferenceCategory => {
  if (!reference) {
    return EExternalReferenceCategory.Unknown;
  }

  const trimmed = reference.trim();

  // Test entries → Unknown
  if (legacyTestPattern.test(trimmed)) {
    return EExternalReferenceCategory.Unknown;
  }

  // New format categories (both modes)
  const newFmt = detectNewFormatCategory(trimmed);
  if (newFmt !== null) {
    return newFmt;
  }

  // Soft mode: legacy non-mission patterns
  if (mode === EExternalReferenceParsingMode.Soft) {
    const legacy = detectLegacyCategorySoft(trimmed);
    if (legacy !== null) {
      return legacy;
    }
  }

  return EExternalReferenceCategory.Unknown;
};

// ─── Donor ID Extraction ─────────────────────────────────────────────────────

/**
 * Extracts donor ID from a text segment.
 *
 * Strategy: find the first alphanumeric token (1-10 chars) containing
 * at least one digit that is NOT a known keyword. Handles organ suffix
 * concatenated to ID (e.g. AMCL417HT).
 *
 * @returns The donor ID, any organ found as suffix, suffix flags,
 *          and remaining text.
 */
const extractDonorId = (
  text: string,
): {
  donorId: string | null;
  organFromSuffix: OrganMatchName | null;
  suffix: DonorSuffix;
  remainingText: string;
} => {
  const nullResult = {
    donorId: null,
    organFromSuffix: null,
    suffix: {
      opoCase: false,
      reOffer: null,
      enhanced: false,
    } as DonorSuffix,
    remainingText: text,
  };

  // Tokenize: keep dashes attached (for suffix extraction like -O, -R1)
  const tokenPattern = /[a-zA-Z0-9][-a-zA-Z0-9]*/g;
  const tokens: Array<{ value: string; index: number }> = [];
  let m: RegExpExecArray | null;
  while ((m = tokenPattern.exec(text)) !== null) {
    tokens.push({ value: m[0], index: m.index });
  }

  for (const token of tokens) {
    let { value } = token;

    // Skip pure keywords
    if (EXCLUDED_TOKENS.has(value.toLowerCase())) {
      continue;
    }

    // Extract suffixes first (-O, -R1, -E, etc.)
    const { cleaned: afterSuffix, suffix } = extractSuffixes(value);
    value = afterSuffix;

    if (value.length === 0) {
      continue;
    }

    // Try to split off organ suffix from end
    let organFromSuffix: OrganMatchName | null = null;
    let idPart = value;

    for (const os of ORGAN_SUFFIXES) {
      if (value.length > os.length && value.toUpperCase().endsWith(os)) {
        const candidateId = value.slice(0, -os.length);
        // The ID part must contain at least one digit
        if (/\d/.test(candidateId) && candidateId.length >= 1) {
          idPart = candidateId;
          const organKey = os.toLowerCase();
          if (
            organKey === 'htki' ||
            organKey === 'liki' ||
            organKey === 'lki'
          ) {
            // Multi-organ suffix: take the first organ only
            // (e.g. HTKI → HT, LIKI → LI)
            organFromSuffix =
              organKey === 'htki' ? 'ht' : ('li' as OrganMatchName);
          } else {
            organFromSuffix = organKey as OrganMatchName;
          }
          break;
        }
      }
    }

    // Validate: must have at least 1 digit, 1-10 chars alphanumeric
    const alphanumOnly = idPart.replace(/[^a-zA-Z0-9]/g, '');
    if (alphanumOnly.length < 1 || alphanumOnly.length > 10) {
      continue;
    }
    if (!/\d/.test(alphanumOnly)) {
      continue;
    }

    // Skip if it's a known keyword after cleaning
    if (EXCLUDED_TOKENS.has(alphanumOnly.toLowerCase())) {
      continue;
    }
    // Skip if it exactly matches an organ keyword (without suffix)
    if (
      !organFromSuffix &&
      organNameMap[alphanumOnly.toLowerCase() as OrganMatchName] !== undefined
    ) {
      continue;
    }

    // Found it — compute remaining text
    const endIndex = token.index + token.value.length;
    const remainingText = text.slice(endIndex);

    return {
      donorId: alphanumOnly,
      organFromSuffix,
      suffix,
      remainingText,
    };
  }

  return nullResult;
};

// ─── Organ Extraction ────────────────────────────────────────────────────────

/**
 * Extracts organ type from text, searching for organ keywords.
 * Uses word boundary matching to avoid partial matches
 * (e.g. "flight" → not "ht").
 */
const extractOrgan = (text: string): OrganMatchName | null => {
  for (const keyword of ORGAN_KEYWORDS) {
    const pattern = new RegExp(`(?:^|[^a-zA-Z])${keyword}(?:$|[^a-zA-Z])`, 'i');
    if (pattern.test(text)) {
      return keyword;
    }
  }
  return null;
};

// ─── Coordinator Extraction ──────────────────────────────────────────────────

/**
 * Extracts coordinator name from segments (typically the last segment
 * after donor type and organ have been identified).
 */
const extractCoordinator = (segments: string[]): string | null => {
  // Work backwards through segments to find the coordinator
  for (let i = segments.length - 1; i >= 0; i--) {
    const seg = segments[i].trim();
    if (!seg) {
      continue;
    }

    // Clean out donor type, organ references, and metadata keywords
    let cleaned = seg
      .replace(DONOR_TYPE_PATTERN, '')
      .replace(/\b(liver|heart|lungs?|kidney|liung|hrt?|live?)\b/gi, '')
      .replace(/\b(ht|li|lu|ki|hr|liv)\b/gi, '')
      .replace(
        /\b(back\s*-?up|backup|bu|pre-?allo(cation|cated)?|recovery|reallocation|reallo)\b/gi,
        '',
      )
      .replace(/\b(is\s+coordinator)\b/gi, '')
      .trim();

    // Remove leading/trailing punctuation
    cleaned = cleaned.replace(/^[^a-zA-Z0-9]+|[^a-zA-Z0-9]+$/g, '').trim();

    if (cleaned && cleaned.length >= 2 && !/^\d+$/.test(cleaned)) {
      if (/[a-zA-Z]/.test(cleaned)) {
        return cleaned;
      }
    }
  }
  return null;
};

// ─── Backup & Pre-allocation Detection ───────────────────────────────────────

const BACKUP_PATTERNS = [
  /\bBACK\s*-?\s*UP\b/i,
  /\bBACKUP\b/i,
  /\(backup\)/i,
  /\*Back\s*-?\s*Up[^*]*\*/i,
];

const PREALLOCATION_PATTERNS = [
  /\bPRE-/i,
  /\bPre\s*-?\s*Allo(cation|cated)?\b/i,
  /\bPREALLO(CATION|CATED)?\b/i,
];

const detectBackup = (text: string): boolean =>
  BACKUP_PATTERNS.some(p => p.test(text));

const detectPreallocation = (text: string): boolean =>
  PREALLOCATION_PATTERNS.some(p => p.test(text));

/**
 * Strips backup and pre-allocation markers from text
 * to ease further parsing.
 */
const stripMetadataMarkers = (text: string): string => {
  let result = text;
  result = result.replace(/\bBACK\s*-?\s*UP\s*[/-]?\s*/gi, '');
  result = result.replace(/\bBACKUP\s*[/-]?\s*/gi, '');
  result = result.replace(/\(backup\)\s*/gi, '');
  result = result.replace(/\*Back\s*-?\s*Up[^*]*\*\s*/gi, '');
  // BU only when it's clearly a separator segment like /BU/
  result = result.replace(/\/\s*BU\s*\//gi, '/');
  result = result.replace(/\s+BU\s*$/i, '');
  // Pre-allocation markers
  result = result.replace(/\bPre\s*-?\s*Allo(cation|cated)?\s*[/-]?\s*/gi, '');
  result = result.replace(/\bPREALLO(CATION|CATED)?\s*/gi, '');
  // Strip leading PRE- prefix (for PRE-AMCL417LI style)
  result = result.replace(/^\s*PRE-/i, '');
  return result.trim();
};

// ─── Normalization ───────────────────────────────────────────────────────────

/**
 * Normalize separator variations and strip surrounding quotes /
 * extra whitespace.
 */
const normalizeReference = (text: string): string => {
  let result = text;
  // Strip surrounding quotes
  result = result.replace(/^["'"]+|["'"]+$/g, '');
  // Normalize separators: backslash → /, double slash → /
  result = result.replace(/\\/g, '/');
  result = result.replace(/\/\//g, '/');
  result = result.replace(/;/g, '/');
  // Collapse multiple spaces
  result = result.replace(/\s+/g, ' ');
  // Strip metadata markers for parsing (flags already detected)
  result = stripMetadataMarkers(result);
  return result.trim();
};

const extractCommandCenterInfo = (text: string): string | null => {
  const slashIndex = text.indexOf('/');
  if (slashIndex === -1) {
    return null;
  }
  const info = text.slice(slashIndex + 1).trim();
  return info || null;
};

// ─── Main Interface ──────────────────────────────────────────────────────────

export interface ParsedDonorReference {
  /** Donor ID (1-10 alphanumeric), or null if not found */
  donorId: string | null;
  /** @deprecated Use donorId instead. Alias for backward compatibility */
  unosId: string | null;
  /** Organ type enum, or null if not recognized / kidney */
  organ: ENOPOrganType | null;
  /** Short organ code (HT, LI, LU, KI), or null */
  organShort: string | null;
  /** Flight category */
  category: EExternalReferenceCategory;
  /** Donor type: DBD or DCD */
  donorType: DonorType | null;
  /** Coordinator name (raw, as-is from input) */
  coordinatorName: string | null;
  /** Whether this is a backup flight */
  isBackup: boolean;
  /** Whether this is a pre-allocation */
  isPreallocation: boolean;
  /** Suffix flags extracted from donor ID (-O, -R1, -E, etc.) */
  suffix: DonorSuffix | null;
  /** Command center info (text after first / in original) */
  commandCenterInfo: string | null;
}

// ─── Single Reference Parsing ────────────────────────────────────────────────

const parseSingleReference = (
  text: string,
  category: EExternalReferenceCategory,
  isBackup: boolean,
  isPreallocation: boolean,
  commandCenterInfo: string | null,
  _mode: EExternalReferenceParsingMode,
  allowOrganOnly = false,
): ParsedDonorReference => {
  const fallback: ParsedDonorReference = {
    donorId: null,
    unosId: null,
    organ: null,
    organShort: null,
    category,
    donorType: null,
    coordinatorName: null,
    isBackup,
    isPreallocation,
    suffix: null,
    commandCenterInfo,
  };

  // Split by / into segments
  const segments = text
    .split('/')
    .map(s => s.trim())
    .filter(Boolean);
  const fullText = segments.join(' ');

  // Extract donor ID from the full text (first matching token)
  const { donorId, organFromSuffix, suffix, remainingText } =
    extractDonorId(fullText);

  if (!donorId) {
    if (!allowOrganOnly) {
      return fallback;
    }
    // In multi-organ context, extract organ for organ-only segments
    // (donor ID will be shared from another segment)
    const organMatch = extractOrgan(fullText);
    if (!organMatch) {
      return fallback;
    }
    const organ = organMatch ? (organNameMap[organMatch] ?? null) : null;
    const organShort = organ
      ? organShortNameMap[organ]
      : organMatch && (organMatch === 'ki' || organMatch === 'kidney')
        ? 'KI'
        : null;
    if (!organShort) {
      return fallback;
    }
    // Extract donor type and coordinator for organ-only segment
    const donorTypeMatch = DONOR_TYPE_PATTERN.exec(fullText);
    const donorType = donorTypeMatch
      ? normalizeDonorType(donorTypeMatch[1])
      : null;
    const coordinatorName = extractCoordinator(
      segments.length > 1 ? segments.slice(1) : [],
    );
    return {
      donorId: null,
      unosId: null,
      organ,
      organShort,
      category,
      donorType,
      coordinatorName,
      isBackup,
      isPreallocation,
      suffix: null,
      commandCenterInfo,
    };
  }

  // Extract donor type
  const donorTypeMatch = DONOR_TYPE_PATTERN.exec(fullText);
  const donorType = donorTypeMatch
    ? normalizeDonorType(donorTypeMatch[1])
    : null;

  // Extract organ: prefer suffix (concatenated to ID),
  // then search remaining text
  let organMatch: OrganMatchName | null = organFromSuffix;
  if (!organMatch) {
    organMatch = extractOrgan(remainingText);
  }
  // Also check full text (minus the donor ID) if remaining
  // didn't yield results
  if (!organMatch) {
    organMatch = extractOrgan(fullText.replace(donorId, ''));
  }

  const organ = organMatch ? (organNameMap[organMatch] ?? null) : null;
  const organShort = organ
    ? organShortNameMap[organ]
    : organMatch && (organMatch === 'ki' || organMatch === 'kidney')
      ? 'KI'
      : null;

  // Extract coordinator from the last meaningful segment(s)
  const coordinatorName = extractCoordinator(
    segments.length > 1 ? segments.slice(1) : [],
  );

  // Update category if we found a donor ID + organ
  const finalCategory =
    donorId && organShort ? EExternalReferenceCategory.Mission : category;

  return {
    donorId,
    unosId: donorId,
    organ,
    organShort,
    category: finalCategory,
    donorType,
    coordinatorName,
    isBackup,
    isPreallocation,
    suffix: hasSuffix(suffix) ? suffix : null,
    commandCenterInfo,
  };
};

// ─── Multi-Organ Parsing ─────────────────────────────────────────────────────

/** Fills in shared donor info across multi-organ results. */
const fillSharedMultiOrganValues = (
  results: ParsedDonorReference[],
  sharedDonorId: string | null,
  sharedDonorType: DonorType | null,
  sharedCoordinator: string | null,
): void => {
  for (const r of results) {
    if (!r.donorId && sharedDonorId) {
      r.donorId = sharedDonorId;
      r.unosId = sharedDonorId;
    }
    if (!r.donorType && sharedDonorType) {
      r.donorType = sharedDonorType;
    }
    if (!r.coordinatorName && sharedCoordinator) {
      r.coordinatorName = sharedCoordinator;
    }
    if (r.donorId && r.organShort) {
      r.category = EExternalReferenceCategory.Mission;
    }
  }
};

const parseMultiOrgan = (
  text: string,
  category: EExternalReferenceCategory,
  isBackup: boolean,
  isPreallocation: boolean,
  commandCenterInfo: string | null,
  mode: EExternalReferenceParsingMode,
): ParsedDonorReference[] => {
  // Split by multi-organ separators (+, &, comma, "and")
  const parts = text
    .split(MULTI_ORGAN_SEPARATORS)
    .map(s => s.trim())
    .filter(Boolean);

  if (parts.length <= 1) {
    return [];
  }

  const results: ParsedDonorReference[] = [];
  let sharedDonorId: string | null = null;
  let sharedDonorType: DonorType | null = null;
  let sharedCoordinator: string | null = null;

  // Parse each segment individually
  for (const part of parts) {
    const parsed = parseSingleReference(
      part,
      category,
      isBackup,
      isPreallocation,
      commandCenterInfo,
      mode,
      true,
    );
    if (parsed.donorId || parsed.organShort) {
      results.push(parsed);
      if (!sharedDonorId && parsed.donorId) {
        sharedDonorId = parsed.donorId;
      }
      if (!sharedDonorType && parsed.donorType) {
        sharedDonorType = parsed.donorType;
      }
      if (!sharedCoordinator && parsed.coordinatorName) {
        sharedCoordinator = parsed.coordinatorName;
      }
    }
  }

  fillSharedMultiOrganValues(
    results,
    sharedDonorId,
    sharedDonorType,
    sharedCoordinator,
  );

  // Multi-organ accepted in all modes (Phase 0: unconditional support)

  return results.filter(r => r.donorId !== null || r.organShort !== null);
};

// ─── Main Parser ─────────────────────────────────────────────────────────────

/**
 * Parses External Booking Reference to extract flight information.
 *
 * Returns an array of ParsedDonorReference — one per organ for
 * multi-organ entries, or a single-element array for single-organ
 * and non-mission entries.
 *
 * **Soft Mode (default)** — Forgiving for real-world data:
 * - Mission: "AMCL417HT / DCD Heart / Amanda"
 * - Accepts 15+ separator styles, organ typos, etc.
 * - Multi-organ: splits into separate results in the array
 *
 * **Strict Mode** — Enforces new format standard:
 * - Mission: "DONORID[ORGAN] / DCD|DBD [OrganFull] / [Coordinator]"
 * - Non-mission: "MX / …", "TRNG / …", "Repo / …", "Special / …"
 * - Rejects multi-organ entries
 *
 * @param donorReference - External booking reference string
 * @param mode - Parsing mode (default: Soft)
 * @returns Array of parsed reference entries
 */
export const parseDonorReference = (
  donorReference = '',
  mode: EExternalReferenceParsingMode = EExternalReferenceParsingMode.Soft,
): ParsedDonorReference[] => {
  const trimmed = donorReference.trim();

  // Detect metadata flags on original text before stripping
  const isBackup = detectBackup(trimmed);
  const isPreallocation = detectPreallocation(trimmed);

  // Category detection on original text
  const category = detectCategory(trimmed, mode);

  // Command center info extraction (legacy compat: text after first /)
  const commandCenterInfo = extractCommandCenterInfo(trimmed);

  // Base result for non-mission categories
  const baseNonMission: ParsedDonorReference = {
    donorId: null,
    unosId: null,
    organ: null,
    organShort: null,
    category,
    donorType: null,
    coordinatorName: null,
    isBackup,
    isPreallocation,
    suffix: null,
    commandCenterInfo,
  };

  // For non-Mission categories, return early
  if (
    category !== EExternalReferenceCategory.Mission &&
    category !== EExternalReferenceCategory.Unknown
  ) {
    return [baseNonMission];
  }

  // ─── Mission / Unknown Parsing ──────────────────────────────────────

  // Normalize the text for parsing
  const normalized = normalizeReference(trimmed);

  // Check for multi-organ (multiple donor IDs or organ separators)
  if (MULTI_ORGAN_SEPARATORS.test(normalized)) {
    const multiResults = parseMultiOrgan(
      normalized,
      category,
      isBackup,
      isPreallocation,
      commandCenterInfo,
      mode,
    );
    if (multiResults.length > 0) {
      return multiResults;
    }
    // Fall through to single parse if multi-organ found nothing
  }

  // Check for organ/organ slash notation (e.g., ABCD123HT/LI)
  const organSlashMatch = ORGAN_SLASH_PATTERN.exec(trimmed);
  if (organSlashMatch) {
    const singleResult = parseSingleReference(
      normalized,
      category,
      isBackup,
      isPreallocation,
      commandCenterInfo,
      mode,
    );
    if (singleResult.donorId) {
      const organ2Key = organSlashMatch[2].toLowerCase() as OrganMatchName;
      const organ2 = organNameMap[organ2Key] ?? null;
      const organ2Short = organ2
        ? organShortNameMap[organ2]
        : organ2Key === 'ki' || organ2Key === 'kidney'
          ? 'KI'
          : null;
      return [
        singleResult,
        {
          ...singleResult,
          organ: organ2,
          organShort: organ2Short,
        },
      ];
    }
  }

  // Single parse
  const result = parseSingleReference(
    normalized,
    category,
    isBackup,
    isPreallocation,
    commandCenterInfo,
    mode,
  );

  if (!result.donorId && category === EExternalReferenceCategory.Unknown) {
    if (mode === EExternalReferenceParsingMode.Strict) {
      console.error(
        '[parseDonorReference] External reference does not match strict format requirements:',
        donorReference,
      );
    } else if (!EXTERNAL_REFERENCE_LEGACY_MODE) {
      console.warn(
        '[parseDonorReference] External reference does not match any known category (legacy mode disabled):',
        donorReference,
      );
    }
  }

  return [result];
};

/**
 * Convenience helper that returns only the first (primary) parsed
 * result. Use this when you only need the primary organ from a
 * multi-organ flight.
 */
export const parseDonorReferencePrimary = (
  donorReference = '',
  mode: EExternalReferenceParsingMode = EExternalReferenceParsingMode.Soft,
): ParsedDonorReference => parseDonorReference(donorReference, mode)[0];
